import { useState } from "react";
import { toast } from "sonner";
import { Field, Card, SaveButton, GhostButton, inputCls, areaCls } from "../ui";
import { adminFetch } from "../adminApi";
import { VEHICLES } from "@/lib/site";

export default function AddonsTab({ content, reload }) {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(content.addons || [])));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const update = (idx, key, value) =>
    setDraft((d) => d.map((a, i) => (i === idx ? { ...a, [key]: value } : a)));

  const updatePerClass = (idx, cat, value) =>
    setDraft((d) =>
      d.map((a, i) => {
        if (i !== idx) return a;
        const ppc = { ...(a.prices_per_class || {}) };
        if (value === "") delete ppc[cat];
        else ppc[cat] = Number(value);
        return { ...a, prices_per_class: ppc };
      })
    );

  const move = (idx, dir) =>
    setDraft((d) => {
      const copy = [...d];
      const j = idx + dir;
      if (j < 0 || j >= copy.length) return d;
      [copy[idx], copy[j]] = [copy[j], copy[idx]];
      return copy.map((a, i) => ({ ...a, order: i + 1 }));
    });

  const remove = (idx) =>
    setDraft((d) => d.filter((_, i) => i !== idx).map((a, i) => ({ ...a, order: i + 1 })));

  const add = () =>
    setDraft((d) => [
      ...d,
      { id: `extra-${Date.now()}`, name: "Neues Extra", desc: "", price: 0, minutes: 15, per_seat: false, active: true, order: d.length + 1, prices_per_class: {} },
    ]);

  const save = async () => {
    setSaving(true);
    try {
      await adminFetch("/admin/content/addons", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft.map((a, i) => ({ ...a, order: i + 1 }))),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      await reload();
      toast.success("Extras gespeichert – live auf der Website.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-zinc-400 leading-relaxed">
        Zusatzleistungen mit Preis (Ab-Preis) und geschätzter Zusatzdauer. Inaktive Extras werden
        auf der Website ausgeblendet, bleiben aber erhalten. Die Reihenfolge im Formular entspricht
        der Anzeige auf der Website (Pfeile zum Sortieren).
      </p>

      {draft.map((a, idx) => (
        <Card
          key={a.id}
          title={a.name || `Extra ${idx + 1}`}
          actions={
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid={`admin-addon-active-${a.id}`}
                onClick={() => update(idx, "active", a.active === false)}
                className={`h-9 rounded-full border px-4 text-xs font-semibold transition-colors ${
                  a.active === false
                    ? "border-zinc-700 text-zinc-400"
                    : "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
                }`}
              >
                {a.active === false ? "Inaktiv" : "Aktiv"}
              </button>
              <GhostButton onClick={() => move(idx, -1)} testid={`admin-addon-up-${a.id}`}>
                ↑
              </GhostButton>
              <GhostButton onClick={() => move(idx, 1)} testid={`admin-addon-down-${a.id}`}>
                ↓
              </GhostButton>
              <GhostButton danger onClick={() => remove(idx)} testid={`admin-addon-delete-${a.id}`}>
                Löschen
              </GhostButton>
            </div>
          }
        >
          <Field label="Name">
            <input data-testid={`admin-addon-name-${a.id}`} className={inputCls} value={a.name || ""} onChange={(e) => update(idx, "name", e.target.value)} maxLength={80} />
          </Field>
          <Field label="Beschreibung">
            <textarea className={areaCls} value={a.desc || ""} onChange={(e) => update(idx, "desc", e.target.value)} maxLength={400} />
          </Field>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Field label="Preis (€, Ab-Preis)">
              <input type="number" min="0" step="0.01" className={inputCls} value={a.price ?? 0} onChange={(e) => update(idx, "price", Number(e.target.value))} />
            </Field>
            <Field label="Dauer (Min.)">
              <input type="number" min="0" className={inputCls} value={a.minutes ?? 0} onChange={(e) => update(idx, "minutes", Number(e.target.value))} />
            </Field>
            <Field label="Pro Sitz abrechnen">
              <button
                type="button"
                data-testid={`admin-addon-perseat-${a.id}`}
                onClick={() => update(idx, "per_seat", !a.per_seat)}
                className={`h-11 w-full rounded-lg border text-sm font-semibold transition-colors ${
                  a.per_seat ? "border-blue-500 bg-blue-500/10 text-blue-400" : "border-zinc-700 bg-zinc-900/60 text-zinc-300"
                }`}
              >
                {a.per_seat ? "Ja – pro Sitz" : "Nein – pauschal"}
              </button>
            </Field>
          </div>

          <div>
            <p className="mb-1.5 font-mono text-[11px] uppercase tracking-wider text-zinc-400">
              Preis je Fahrzeugklasse (optional – leer = Standardpreis)
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {VEHICLES.map((v) => (
                <Field key={v.id} label={v.label}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className={inputCls}
                    value={a.prices_per_class?.[v.id] ?? ""}
                    onChange={(e) => updatePerClass(idx, v.id, e.target.value)}
                    placeholder={String(a.price ?? 0)}
                  />
                </Field>
              ))}
            </div>
          </div>

          {(content.packages || []).some((p) => (p.optional_addon_ids || []).includes(a.id)) ? (
            <p className="text-xs text-zinc-400">
              Wird als optional für: {content.packages.filter((p) => (p.optional_addon_ids || []).includes(a.id)).map((p) => p.name).join(", ")}
            </p>
          ) : (
            <p className="text-xs text-zinc-400">Noch keinem Paket als optional zugeordnet (im Tab „Pakete &amp; Preise" zuweisen).</p>
          )}
        </Card>
      ))}

      <div className="sticky bottom-4 flex flex-wrap items-center gap-3 rounded-2xl border border-[#27272A] bg-[#0C0C0E]/95 p-4 backdrop-blur">
        <SaveButton onClick={save} saving={saving} saved={saved} testid="admin-save-addons" />
        <GhostButton onClick={add} testid="admin-addon-add">+ Extra hinzufügen</GhostButton>
        <GhostButton onClick={() => setDraft(JSON.parse(JSON.stringify(content.addons || [])))} testid="admin-reset-addons">
          Verwerfen
        </GhostButton>
      </div>
    </div>
  );
}
